import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { DEEP_ANALYSIS_LABEL } from '@/hooks/useAnalysisSet';
import type { AnalysisSetStatus } from '@/hooks/useDocuments';

const INACTIVE_VISIBLE_ANALYSIS_SET_STATUSES = new Set(['removing', 'error']);

interface AnalysisSetBadgeProps {
  active?: boolean;
  status?: AnalysisSetStatus | string | null;
  compact?: boolean;
  iconOnly?: boolean;
  tooltip?: string;
  className?: string;
}

const STATUS_CONFIG: Record<string, { label: string; dotClassName: string; textClassName: string; tooltip: string }> = {
  included: {
    label: 'Included',
    dotClassName: 'bg-amber-500/50',
    textClassName: 'text-amber-800',
    tooltip: 'Included in Deep Analysis and waiting to be integrated',
  },
  queued: {
    label: 'Queued',
    dotClassName: 'bg-amber-500/50',
    textClassName: 'text-amber-800',
    tooltip: 'Queued for Deep Analysis integration',
  },
  processing: {
    label: 'Processing',
    dotClassName: 'bg-amber-500/50',
    textClassName: 'text-amber-800',
    tooltip: 'Queued for Deep Analysis integration',
  },
  current: {
    label: 'Current',
    dotClassName: 'bg-teal-600/55',
    textClassName: 'text-teal-800',
    tooltip: 'Document is up to date in Deep Analysis',
  },
  removing: {
    label: 'Removing',
    dotClassName: 'bg-orange-300/70',
    textClassName: 'text-orange-700',
    tooltip: 'Removing from Deep Analysis',
  },
  removed: {
    label: 'Not included',
    dotClassName: 'bg-gray-300',
    textClassName: 'text-gray-400',
    tooltip: 'This document is not included in Deep Analysis',
  },
  error: {
    label: 'Error',
    dotClassName: 'bg-rose-400/70',
    textClassName: 'text-rose-700',
    tooltip: 'Deep Analysis could not complete for this document',
  },
};

export function visibleAnalysisSetStatus(document: {
  analysisSetEffective?: boolean;
  analysisSetStatus?: AnalysisSetStatus | null;
}) {
  const status = document.analysisSetStatus;
  if (!status) return null;
  if (document.analysisSetEffective) return status;
  return INACTIVE_VISIBLE_ANALYSIS_SET_STATUSES.has(status) ? status : null;
}

export function AnalysisSetBadge({
  active,
  status,
  compact = false,
  iconOnly = false,
  tooltip,
  className,
}: AnalysisSetBadgeProps) {
  const effectiveStatus = status || (active ? 'included' : null);
  const config = effectiveStatus ? STATUS_CONFIG[effectiveStatus] : null;
  if (!config) return null;

  const badge = iconOnly ? (
    <span
      role="img"
      aria-label={`${DEEP_ANALYSIS_LABEL}: ${config.label}`}
      className={cn(
        'block rounded-full',
        compact ? 'h-2 w-2' : 'h-2.5 w-2.5',
        config.dotClassName,
        className,
      )}
    />
  ) : (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm font-medium leading-none',
        config.textClassName,
        compact ? 'text-[11px]' : 'text-xs',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'rounded-full',
          compact ? 'h-1.5 w-1.5' : 'h-2 w-2',
          config.dotClassName,
        )}
      />
      {compact && active && !status ? DEEP_ANALYSIS_LABEL : config.label}
    </span>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {tooltip ?? config.tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
